import { createPortal } from 'react-dom';
import { useEffect, useMemo, useState, type Dispatch, type SetStateAction } from 'react';
import { Copy, CreditCard, Eye, EyeOff, Loader2, MoreHorizontal, RefreshCw, ShieldCheck, Tag, Users, X } from 'lucide-react';
import { toast } from 'sonner';
import { useSearchParams } from 'react-router-dom';

import AdminShell from '../components/AdminShell';
import PaginationControls from '../components/PaginationControls';
import { useAdminCatalog } from '../hooks/useAdminCatalog';
import { useAdminSubscriptions } from '../hooks/useAdminSubscriptions';
import { useAdminOffers } from '../hooks/useAdminOffers';
import { useLockBodyScroll } from '../hooks/useLockBodyScroll';
import type { AdminOfferRow, AdminPlanRow, ProductFeatureDefinition } from '../api/admin';
import { deleteAdminPlan, syncAdminPlanToRazorpay, syncAdminOfferToRazorpay } from '../api/admin';

const getDefaultFeaturesForSchema = (schema: ProductFeatureDefinition[]) => schema.reduce<Record<string, unknown>>((accumulator, definition) => {
  accumulator[definition.key] = definition.defaultValue ?? (definition.type === 'boolean' ? false : null);
  return accumulator;
}, {});

const normalizeFeatures = (schema: ProductFeatureDefinition[], features?: Record<string, unknown> | null): Record<string, unknown> => ({
  ...getDefaultFeaturesForSchema(schema),
  ...(features || {}),
});

const toFeatureNumber = (value: unknown) => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const getFeaturePayload = (schema: ProductFeatureDefinition[], features: Record<string, unknown>) =>
  schema.reduce<Record<string, unknown>>((accumulator, definition) => {
    const rawValue = features[definition.key];
    if (definition.type === 'number') {
      accumulator[definition.key] = toFeatureNumber(rawValue);
    } else if (definition.type === 'boolean') {
      accumulator[definition.key] = Boolean(rawValue);
    } else if (rawValue === null || rawValue === undefined) {
      accumulator[definition.key] = null;
    } else {
      accumulator[definition.key] = String(rawValue);
    }
    return accumulator;
  }, {});

const getFeatureSummary = (definition: ProductFeatureDefinition, value: unknown) => {
  if (definition.type === 'boolean') {
    return Boolean(value) ? definition.label : null;
  }

  if (value === null || value === undefined || value === '') {
    return definition.nullable ? (definition.nullableLabel || definition.label) : null;
  }

  if (definition.type === 'number') {
    return `${Number(value).toLocaleString('en-IN')} ${definition.label.toLowerCase()}`;
  }

  return `${definition.label}: ${String(value)}`;
};

const currencyFormat = (amount: number, currency: string) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: currency || 'INR',
    maximumFractionDigits: 2,
  }).format(amount / 100);

export default function SubscriptionsPage() {
  const [searchParams] = useSearchParams();
  const [selectedProductCode, setSelectedProductCode] = useState<string>(searchParams.get('product') || 'viewer-lens');
  const { overview, loadingOverview, creatingPlan, refreshOverview, createPlan } = useAdminSubscriptions(selectedProductCode);
  const [planQuery, setPlanQuery] = useState('');
  const [planPage, setPlanPage] = useState(1);
  const [subscriptionQuery, setSubscriptionQuery] = useState('');
  const [subscriptionPage, setSubscriptionPage] = useState(1);
  const plansCatalog = useAdminCatalog(selectedProductCode, planQuery, planPage, 8, {
    loadPlans: true,
    loadSubscriptions: false,
  });
  const subscriptionsCatalog = useAdminCatalog(selectedProductCode, subscriptionQuery, subscriptionPage, 8, {
    loadPlans: false,
    loadSubscriptions: true,
  });
  const { plans, plansPagination, savePlan, removePlan, mutating: mutatingPlans, loading: loadingPlans } = plansCatalog;
  const { subscriptions, subscriptionsPagination, revokeSubscription, resetMonthlyQuota, mutating: mutatingSubscriptions, loading: loadingSubscriptions } = subscriptionsCatalog;
  const selectedProduct = overview?.products?.find((product) => product.code === selectedProductCode) || null;
  const activeFeatureSchema = selectedProduct?.featureSchema || [];

  const [draft, setDraft] = useState({
    code: '',
    name: '',
    description: '',
    amount: '59900',
    currency: 'INR',
    intervalUnit: 'monthly',
    intervalCount: '1',
    isDefault: true,
    isPublic: true,
    isPopular: false,
    showOnLanding: true,
    features: {} as Record<string, unknown>,
  });
  const [editingPlanId, setEditingPlanId] = useState<number | null>(null);
  const [createPlanOpen, setCreatePlanOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState<null | {
    top: number;
    right: number;
    items: Array<{
      label: string;
      tone?: 'default' | 'danger';
      disabled?: boolean;
      onClick: () => void;
    }>;
  }>(null);
  const [confirmAction, setConfirmAction] = useState<null | {
    title: string;
    message: string;
    confirmLabel: string;
    tone?: 'default' | 'danger';
    action: () => Promise<void>;
  }>(null);
  const [editingPlanDraft, setEditingPlanDraft] = useState({
    name: '',
    description: '',
    amount: '',
    currency: 'INR',
    intervalUnit: 'monthly',
    intervalCount: '1',
    status: 'active',
    isDefault: false,
    isPublic: true,
    isPopular: false,
    showOnLanding: true,
    features: {} as Record<string, unknown>,
  });
  const [revokeTarget, setRevokeTarget] = useState<{ id: number; email: string } | null>(null);
  const [cancelAtCycleEnd, setCancelAtCycleEnd] = useState(true);
  const [syncingPlanId, setSyncingPlanId] = useState<number | null>(null);

  // ── Offers ────────────────────────────────────────────────────────────────
  const { offers, loading: loadingOffers, mutating: mutatingOffers, refresh: refreshOffers, createOffer, updateOffer, removeOffer } = useAdminOffers(selectedProductCode);
  const [offerModalOpen, setOfferModalOpen] = useState(false);
  const [editingOfferId, setEditingOfferId] = useState<number | null>(null);
  // Per-method Razorpay ID linking: key = "${offerId}:${method}"
  const [methodInputMap, setMethodInputMap] = useState<Record<string, string>>({});
  const [syncingKey, setSyncingKey] = useState<string | null>(null);
  const [expandedRzpOffer, setExpandedRzpOffer] = useState<number | null>(null);

  const PAYMENT_METHODS = [
    { key: 'card',       label: 'Card',       icon: '💳' },
    { key: 'upi',        label: 'UPI',        icon: '📱' },
    { key: 'netbanking', label: 'Netbanking', icon: '🏦' },
    { key: 'wallet',     label: 'Wallet',     icon: '👜' },
  ] as const;

  const emptyOfferDraft = () => ({
    code: '',
    title: '',
    description: '',
    discountType: 'percentage' as 'percentage' | 'fixed',
    discountValue: '10',
    maxRedemptions: '',
    validFrom: new Date().toISOString().slice(0, 10),
    validUntil: '',
    showBanner: true,
    bannerText: '',
  });

  const [offerDraft, setOfferDraft] = useState(emptyOfferDraft);

  const openCreateOfferModal = () => {
    setEditingOfferId(null);
    setOfferDraft(emptyOfferDraft());
    setOfferModalOpen(true);
  };

  const openEditOfferModal = (offer: AdminOfferRow) => {
    setEditingOfferId(offer.id);
    setOfferDraft({
      code: offer.code,
      title: offer.title,
      description: offer.description || '',
      discountType: offer.discountType,
      discountValue: String(offer.discountValue / 100),
      maxRedemptions: offer.maxRedemptions != null ? String(offer.maxRedemptions) : '',
      validFrom: offer.validFrom.slice(0, 10),
      validUntil: offer.validUntil ? offer.validUntil.slice(0, 10) : '',
      showBanner: offer.showBanner,
      bannerText: offer.bannerText || '',
    });
    setOfferModalOpen(true);
  };

  const handleDuplicateOffer = (offer: AdminOfferRow) => {
    setEditingOfferId(null);
    setOfferDraft({
      code: offer.code + '-copy',
      title: offer.title + ' (Copy)',
      description: offer.description || '',
      discountType: offer.discountType,
      discountValue: String(offer.discountValue / 100),
      maxRedemptions: offer.maxRedemptions != null ? String(offer.maxRedemptions) : '',
      validFrom: new Date().toISOString().slice(0, 10),
      validUntil: offer.validUntil ? offer.validUntil.slice(0, 10) : '',
      showBanner: offer.showBanner,
      bannerText: offer.bannerText || '',
    });
    setOfferModalOpen(true);
  };

  const closeOfferModal = () => {
    setOfferModalOpen(false);
    setEditingOfferId(null);
  };

  const handleCreateOffer = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!offerDraft.code.trim()) { toast.error('Offer code is required'); return; }
    if (!offerDraft.title.trim()) { toast.error('Title is required'); return; }
    if (!Number.isFinite(Number(offerDraft.discountValue)) || Number(offerDraft.discountValue) <= 0) {
      toast.error('Discount value must be a positive number');
      return;
    }
    try {
      await createOffer({
        productCode: selectedProductCode,
        code: offerDraft.code.trim(),
        title: offerDraft.title.trim(),
        description: offerDraft.description.trim() || undefined,
        discountType: offerDraft.discountType,
        discountValue: Math.round(Number(offerDraft.discountValue) * 100),
        maxRedemptions: offerDraft.maxRedemptions ? Number(offerDraft.maxRedemptions) : null,
        validFrom: offerDraft.validFrom,
        validUntil: offerDraft.validUntil || null,
        showBanner: offerDraft.showBanner,
        bannerText: offerDraft.bannerText.trim() || undefined,
      });
      toast.success('Offer created.');
      closeOfferModal();
    } catch (err: any) {
      toast.error(err?.response?.data?.error?.message || err?.message || 'Failed to create offer.');
    }
  };

  const handleSyncMethodOffer = async (offerId: number, method: string) => {
    const k = `${offerId}:${method}`;
    const rzpId = (methodInputMap[k] || '').trim();
    if (!rzpId) {
      toast.error('Paste the Razorpay offer ID first (e.g. off_XXXX).');
      return;
    }
    setSyncingKey(k);
    try {
      await syncAdminOfferToRazorpay(offerId, rzpId, method);
      toast.success(`Linked ${method} → ${rzpId}`);
      setMethodInputMap((prev) => { const n = { ...prev }; delete n[k]; return n; });
      refreshOffers();
    } catch (error: any) {
      toast.error(error?.response?.data?.error?.message || error?.message || 'Failed to link.');
    } finally {
      setSyncingKey(null);
    }
  };

  const handleDeleteOffer = async (offerId: number) => {
    if (!window.confirm('Are you sure you want to delete this offer?')) return;
    try {
      await removeOffer(offerId);
      toast.success('Offer deleted.');
    } catch (err: any) {
      toast.error(err?.response?.data?.error?.message || err?.message || 'Failed to delete offer.');
    }
  };

  const handleUpdateOffer = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingOfferId) return;
    try {
      await updateOffer(editingOfferId, {
        title: offerDraft.title.trim(),
        description: offerDraft.description.trim() || null,
        discountType: offerDraft.discountType,
        discountValue: Math.round(Number(offerDraft.discountValue) * 100),
        maxRedemptions: offerDraft.maxRedemptions ? Number(offerDraft.maxRedemptions) : null,
        validFrom: offerDraft.validFrom,
        validUntil: offerDraft.validUntil || null,
        showBanner: offerDraft.showBanner,
        bannerText: offerDraft.bannerText.trim() || null,
      });
      toast.success('Offer updated.');
      closeOfferModal();
    } catch (err: any) {
      toast.error(err?.response?.data?.error?.message || err?.message || 'Failed to update offer.');
    }
  };

  // Scroll lock for modals and menus
  useLockBodyScroll(createPlanOpen || editingPlanId !== null || Boolean(confirmAction) || Boolean(openMenu) || Boolean(revokeTarget) || offerModalOpen);

  const closePlanModal = () => {
    setCreatePlanOpen(false);
    setEditingPlanId(null);
  };

  const openCreatePlanModal = () => {
    setEditingPlanId(null);
    setDraft({
      code: '',
      name: '',
      description: '',
      amount: '59900',
      currency: 'INR',
      intervalUnit: 'monthly',
      intervalCount: '1',
      isDefault: false,
      isPublic: true,
      isPopular: false,
      showOnLanding: true,
      features: getDefaultFeaturesForSchema(activeFeatureSchema),
    });
    setCreatePlanOpen(true);
  };

  const summaryCards = useMemo(() => {
    if (!overview) return [];
    return [
      { label: 'Total subscriptions', value: overview.summary.total, icon: CreditCard },
      { label: 'Active', value: overview.summary.active, icon: ShieldCheck },
      { label: 'Pending', value: overview.summary.pending, icon: RefreshCw },
      { label: 'Cancelled/Halted', value: overview.summary.cancelled + overview.summary.halted, icon: Users },
    ];
  }, [overview]);

  useEffect(() => {
    if (!openMenu) return undefined;

    const handleClose = () => setOpenMenu(null);
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpenMenu(null);
    };

    document.addEventListener('mousedown', handleClose);
    document.addEventListener('keydown', handleEscape);
    window.addEventListener('resize', handleClose);
    window.addEventListener('scroll', handleClose, true);

    return () => {
      document.removeEventListener('mousedown', handleClose);
      document.removeEventListener('keydown', handleEscape);
      window.removeEventListener('resize', handleClose);
      window.removeEventListener('scroll', handleClose, true);
    };
  }, [openMenu]);

  const handleCreatePlan = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!draft.code.trim()) {
      toast.error('Plan code is required', { description: 'Enter a unique identifier for this plan (e.g. "basic-monthly").' });
      return;
    }
    if (!draft.name.trim()) {
      toast.error('Plan name is required');
      return;
    }
    if (draft.amount === '' || draft.amount === null || draft.amount === undefined || isNaN(Number(draft.amount))) {
      toast.error('Amount is required', { description: 'Enter the plan price in paise (0 for free/beta plans).' });
      return;
    }
    if (!draft.intervalUnit) {
      toast.error('Billing interval is required');
      return;
    }
    try {
      await createPlan({
        productCode: selectedProductCode,
        code: draft.code.trim(),
        name: draft.name.trim(),
        description: draft.description.trim(),
        amount: Number(draft.amount),
        currency: draft.currency.trim() || 'INR',
        intervalUnit: draft.intervalUnit,
        intervalCount: Number(draft.intervalCount || 1),
        isDefault: draft.isDefault,
        isPublic: draft.isPublic,
        isPopular: draft.isPopular,
        showOnLanding: draft.showOnLanding,
        features: getFeaturePayload(activeFeatureSchema, draft.features),
      });
      toast.success('Subscription plan created.');
      setDraft({
        code: '',
        name: '',
        description: '',
        amount: '59900',
        currency: 'INR',
        intervalUnit: 'monthly',
        intervalCount: '1',
        isDefault: false,
        isPublic: true,
        isPopular: false,
        showOnLanding: true,
        features: getDefaultFeaturesForSchema(activeFeatureSchema),
      });
      closePlanModal();
    } catch (error: any) {
      toast.error(error?.response?.data?.error?.message || error?.message || 'Failed to create plan.');
    }
  };

  const startPlanEdit = (plan: AdminPlanRow) => {
    setEditingPlanId(plan.id);
    setEditingPlanDraft({
      name: plan.name,
      description: plan.description || '',
      amount: String(plan.amount),
      currency: plan.currency,
      intervalUnit: plan.intervalUnit,
      intervalCount: String(plan.intervalCount),
      status: plan.status,
      isDefault: plan.isDefault,
      isPublic: plan.isPublic ?? true,
      isPopular: plan.isPopular ?? false,
      showOnLanding: plan.showOnLanding ?? true,
      features: normalizeFeatures(activeFeatureSchema, plan.features),
    });
    setCreatePlanOpen(true);
  };

  const duplicatePlan = (plan: AdminPlanRow) => {
    setEditingPlanId(null);
    setDraft({
      code: '',
      name: `Copy of ${plan.name}`,
      description: plan.description || '',
      amount: String(plan.amount),
      currency: plan.currency,
      intervalUnit: plan.intervalUnit,
      intervalCount: String(plan.intervalCount),
      isDefault: false,
      isPublic: plan.isPublic ?? true,
      isPopular: plan.isPopular ?? false,
      showOnLanding: plan.showOnLanding ?? true,
      features: normalizeFeatures(activeFeatureSchema, plan.features),
    });
    setCreatePlanOpen(true);
  };

  const handleSavePlan = async (planId: number) => {
    try {
      await savePlan(planId, {
        name: editingPlanDraft.name.trim(),
        description: editingPlanDraft.description.trim(),
        amount: Number(editingPlanDraft.amount),
        currency: editingPlanDraft.currency,
        intervalUnit: editingPlanDraft.intervalUnit,
        intervalCount: Number(editingPlanDraft.intervalCount),
        status: editingPlanDraft.status,
        isDefault: editingPlanDraft.isDefault,
        isPublic: editingPlanDraft.isPublic,
        isPopular: editingPlanDraft.isPopular,
        showOnLanding: editingPlanDraft.showOnLanding,
        features: getFeaturePayload(activeFeatureSchema, editingPlanDraft.features),
      });
      toast.success('Plan updated.');
      closePlanModal();
      await refreshOverview();
    } catch (error: any) {
      toast.error(error?.response?.data?.error?.message || error?.message || 'Failed to update plan.');
    }
  };

  const renderFeatureEditor = (
    schema: ProductFeatureDefinition[],
    features: Record<string, unknown>,
    setFeatures: Dispatch<SetStateAction<Record<string, unknown>>>,
  ) => (
    <div className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-3 space-y-3">
      <div>
        <p className="text-xs font-semibold text-zinc-300">Plan features</p>
        <p className="text-[11px] text-zinc-500 mt-1">These fields are defined by the selected product and drive its entitlements.</p>
      </div>
      {!schema.length ? (
        <p className="text-xs text-zinc-500">No feature schema is defined for this product yet. Add it from the Products module first.</p>
      ) : null}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {schema.map((definition) => {
          const isAdminOnly = definition.adminVisible === false;
          if (definition.type === 'boolean') {
            return (
              <label key={definition.key} className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm text-zinc-300 ${isAdminOnly ? 'border-amber-800/40 bg-amber-950/30' : 'border-zinc-800 bg-zinc-900'}`}>
                <input
                  type="checkbox"
                  checked={Boolean(features[definition.key])}
                  onChange={(e) => setFeatures((prev) => ({ ...prev, [definition.key]: e.target.checked }))}
                />
                <span>{definition.label}</span>
                {isAdminOnly && <span className="ml-auto text-[9px] text-amber-600 font-mono">admin only</span>}
              </label>
            );
          }

          if (definition.type === 'select') {
            return (
              <div key={definition.key}>
                <label className="flex items-center gap-1.5 text-[11px] text-zinc-500">{definition.label}{isAdminOnly && <span className="text-[9px] text-amber-600 font-mono">admin only</span>}</label>
                <select
                  value={String(features[definition.key] ?? definition.defaultValue ?? '')}
                  onChange={(e) => setFeatures((prev) => ({ ...prev, [definition.key]: e.target.value }))}
                  className="mt-2 h-10 w-full px-3 bg-zinc-900 border border-zinc-700 rounded-xl text-sm text-zinc-100"
                >
                  {(definition.options || []).map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
            );
          }

          if (definition.type === 'number') {
            const isUnlimited = definition.nullable && (features[definition.key] === null || features[definition.key] === undefined || features[definition.key] === '');
            return (
              <div key={definition.key}>
                <label className="flex items-center gap-1.5 text-[11px] text-zinc-500">{definition.label}{isAdminOnly && <span className="text-[9px] text-amber-600 font-mono">admin only</span>}</label>
                <div className="mt-2 flex items-center gap-2">
                  <input
                    type="number"
                    min={definition.min}
                    max={definition.max}
                    value={isUnlimited ? '' : String(features[definition.key] ?? '')}
                    placeholder={definition.label}
                    disabled={isUnlimited}
                    onChange={(e) => setFeatures((prev) => ({ ...prev, [definition.key]: e.target.value ? Number(e.target.value) : null }))}
                    className="h-10 min-w-0 flex-1 px-3 bg-zinc-900 border border-zinc-700 rounded-xl text-sm text-zinc-100 disabled:opacity-50"
                  />
                  {definition.nullable ? (
                    <label className="flex shrink-0 items-center gap-2 rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-300">
                      <input
                        type="checkbox"
                        checked={isUnlimited}
                        onChange={(e) => setFeatures((prev) => ({
                          ...prev,
                          [definition.key]: e.target.checked ? null : (definition.defaultValue ?? definition.min ?? 1),
                        }))}
                      />
                      <span>{definition.nullableLabel || 'Unlimited'}</span>
                    </label>
                  ) : null}
                </div>
              </div>
            );
          }

          return (
            <div key={definition.key}>
              <label className="flex items-center gap-1.5 text-[11px] text-zinc-500">{definition.label}{isAdminOnly && <span className="text-[9px] text-amber-600 font-mono">admin only</span>}</label>
              <input
                value={String(features[definition.key] ?? '')}
                onChange={(e) => setFeatures((prev) => ({ ...prev, [definition.key]: e.target.value }))}
                className="mt-2 h-10 w-full px-3 bg-zinc-900 border border-zinc-700 rounded-xl text-sm text-zinc-100"
              />
            </div>
          );
        })}
      </div>
    </div>
  );

  const handleDeletePlan = async (planId: number) => {
    try {
      const res = await deleteAdminPlan(planId);
      const data = res?.data?.data;
      if (data?.fallbackAction === 'deactivated') {
        toast.warning(`Plan has ${data.activeSubscriptionCount} active subscriber(s) — deactivated instead of deleted.`);
      } else {
        toast.success('Plan deleted.');
      }
      if (editingPlanId === planId) setEditingPlanId(null);
      await refreshOverview();
    } catch (error: any) {
      toast.error(error?.response?.data?.error?.message || error?.message || 'Failed to remove plan.');
    }
  };

  const handleSyncPlanToRazorpay = async (planId: number) => {
    setSyncingPlanId(planId);
    try {
      const res = await syncAdminPlanToRazorpay(planId);
      toast.success(`Synced to Razorpay: ${res.data.data.razorpayPlanId}`);
      await plansCatalog.refresh?.();
    } catch (error: any) {
      toast.error(error?.response?.data?.error?.message || error?.message || 'Failed to sync plan to Razorpay.');
    } finally {
      setSyncingPlanId(null);
    }
  };

  const handleRevokeSubscription = (subscriptionId: number, email: string) => {
    setRevokeTarget({ id: subscriptionId, email });
    setCancelAtCycleEnd(true);
  };

  const executeRevoke = async () => {
    if (!revokeTarget) return;
    try {
      await revokeSubscription(revokeTarget.id, cancelAtCycleEnd);
      toast.success('Subscription cancelled.');
      setRevokeTarget(null);
      await refreshOverview();
    } catch (error: any) {
      toast.error(error?.response?.data?.error?.message || error?.message || 'Failed to cancel subscription.');
    }
  };

  const handleResetMonthlyQuota = async (userId: number, productCode: string, type: 'analyses' | 'reanalyses' | 'both' = 'both') => {
    try {
      await resetMonthlyQuota(userId, productCode, type);
      toast.success('Monthly quota usage reset.');
      await refreshOverview();
    } catch (error: any) {
      toast.error(error?.response?.data?.error?.message || error?.message || 'Failed to reset monthly quota.');
    }
  };

  const runConfirmedAction = async () => {
    if (!confirmAction) return;
    await confirmAction.action();
    setConfirmAction(null);
  };

  const openActionMenu = (
    event: React.MouseEvent<HTMLButtonElement>,
    items: Array<{ label: string; tone?: 'default' | 'danger'; disabled?: boolean; onClick: () => void }>,
  ) => {
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();
    setOpenMenu({
      top: rect.bottom + 8,
      right: Math.max(window.innerWidth - rect.right, 16),
      items,
    });
  };

  return (
    <AdminShell
      title="Subscriptions"
      description="Manage product-linked plans and recurring billing state across Novera Labs."
      actions={(
        <>
          <select
            value={selectedProductCode}
            onChange={(event) => {
              setSelectedProductCode(event.target.value);
              setPlanPage(1);
              setSubscriptionPage(1);
              setEditingPlanId(null);
              setCreatePlanOpen(false);
              setOfferModalOpen(false);
            }}
            className="h-9 px-3 rounded-lg border border-zinc-700 bg-zinc-900 text-zinc-100 text-xs"
          >
            {overview?.products?.map((product) => (
              <option key={product.id} value={product.code}>{product.name}</option>
            ))}
          </select>
          <button
            onClick={() => refreshOverview().catch(() => undefined)}
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-zinc-700 text-zinc-300 hover:border-zinc-600 hover:text-zinc-100 transition-colors text-xs"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
          <button
            type="button"
            onClick={openCreatePlanModal}
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-amber-500 hover:bg-amber-600 text-black transition-colors text-xs font-semibold"
          >
            Create plan
          </button>
          <button
            type="button"
            onClick={openCreateOfferModal}
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white transition-colors text-xs font-semibold"
          >
            <Tag className="w-3.5 h-3.5" /> Create offer
          </button>
        </>
      )}
    >
      {loadingOverview && !overview ? (
        <div className="flex items-center gap-2 text-zinc-500">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading subscription overview…
        </div>
      ) : null}

      {overview ? (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            {summaryCards.map(({ label, value, icon: Icon }) => (
              <div key={label} className="rounded-2xl border border-zinc-800 bg-zinc-900 p-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-zinc-400">{label}</span>
                  <Icon className="w-4 h-4 text-amber-500" />
                </div>
                <p className="mt-3 text-2xl font-semibold text-zinc-100">{value}</p>
              </div>
            ))}
          </div>

          <div>
            <section className="rounded-2xl border border-zinc-800 bg-zinc-900 p-5">
              <div className="mb-4">
                <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-zinc-100">Subscription Plans</h2>
                    <p className="text-xs text-zinc-500">Plan catalog for the selected product.</p>
                  </div>
                  <div className="w-full max-w-md">
                    <input
                      value={planQuery}
                      onChange={(e) => {
                        setPlanQuery(e.target.value);
                        setPlanPage(1);
                      }}
                      placeholder="Search plans"
                      className="w-full h-10 px-3 bg-zinc-950 border border-zinc-700 rounded-xl text-sm text-zinc-100"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                {loadingPlans ? (
                  <div className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-4 text-sm text-zinc-400 inline-flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Loading subscription plans...
                  </div>
                ) : null}
                {!loadingPlans && plans.length === 0 ? (
                  <div className="rounded-xl border border-dashed border-zinc-700 bg-zinc-950/40 p-6 text-sm text-zinc-500">
                    No subscription plans found for this product.
                  </div>
                ) : null}
                {!loadingPlans ? plans.map((plan) => (
                  <div key={plan.id} className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="text-sm font-semibold text-zinc-100">{plan.name}</p>
                        <p className="text-xs text-zinc-500 mt-1">{plan.code}</p>
                        {plan.description ? <p className="text-xs text-zinc-400 mt-2">{plan.description}</p> : null}
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold text-amber-400">{currencyFormat(plan.amount, plan.currency)}</p>
                        <p className="text-xs text-zinc-500">{plan.intervalCount} × {plan.intervalUnit}</p>
                      </div>
                    </div>
                    <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px]">
                      <span className={`px-2 py-1 rounded-full border ${plan.status === 'active' ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400' : 'border-zinc-700 bg-zinc-800 text-zinc-400'}`}>
                        {plan.status}
                      </span>
                      {plan.isDefault ? <span className="px-2 py-1 rounded-full border border-amber-500/30 bg-amber-500/10 text-amber-400">Default</span> : null}
                      <span className={`px-2 py-1 rounded-full border flex items-center gap-1 ${plan.isPublic ? 'border-sky-500/30 bg-sky-500/10 text-sky-400' : 'border-zinc-600 bg-zinc-800/60 text-zinc-500'}`}>
                        {plan.isPublic ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                        {plan.isPublic ? 'Public' : 'Private'}
                      </span>
                      <span className={`px-2 py-1 rounded-full border ${plan.showOnLanding ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400' : 'border-zinc-600 bg-zinc-800/60 text-zinc-500'}`}>
                        {plan.showOnLanding ? 'Landing: On' : 'Landing: Off'}
                      </span>
                      <span className={`px-2 py-1 rounded-full border ${plan.isPopular ? 'border-fuchsia-500/30 bg-fuchsia-500/10 text-fuchsia-400' : 'border-zinc-600 bg-zinc-800/60 text-zinc-500'}`}>
                        {plan.isPopular ? 'Popular' : 'Not popular'}
                      </span>
                      {activeFeatureSchema
                        .map((definition) => ({ definition, summary: getFeatureSummary(definition, normalizeFeatures(activeFeatureSchema, plan.features)[definition.key]) }))
                        .filter(({ summary }) => Boolean(summary))
                        .slice(0, 4)
                        .map(({ definition, summary }) => (
                          <span
                            key={`${plan.id}-${summary}`}
                            className={`px-2 py-1 rounded-full border text-zinc-300 ${definition.adminVisible === false ? 'border-amber-800/40 bg-amber-950/30 text-amber-400' : 'border-zinc-700 bg-zinc-900'}`}
                          >
                            {summary}
                          </span>
                        ))}
                      <span className="text-zinc-500">
                        Razorpay:{' '}
                        {plan.amount <= 0
                          ? <span className="text-zinc-600 italic">N/A (free plan)</span>
                          : plan.razorpayPlanId
                            ? <span className="font-mono text-emerald-400">{plan.razorpayPlanId}</span>
                            : <span className="text-amber-400">not synced</span>}
                      </span>
                    </div>
                    <div className="mt-3 flex items-center gap-2">
                      <button
                        type="button"
                        disabled={mutatingPlans}
                        onClick={() => savePlan(plan.id, { isPublic: !plan.isPublic })}
                        className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border text-[11px] font-medium transition-colors disabled:opacity-50 ${plan.isPublic ? 'border-sky-500/30 bg-sky-500/10 text-sky-400 hover:bg-sky-500/20' : 'border-zinc-700 bg-zinc-800/60 text-zinc-400 hover:border-zinc-600 hover:text-zinc-300'}`}
                      >
                        {plan.isPublic ? <><EyeOff className="w-3 h-3" /> Make private</> : <><Eye className="w-3 h-3" /> Make public</>}
                      </button>
                      <button
                        type="button"
                        disabled={mutatingPlans}
                        onClick={() => savePlan(plan.id, { showOnLanding: !plan.showOnLanding })}
                        className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border text-[11px] font-medium transition-colors disabled:opacity-50 ${plan.showOnLanding ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20' : 'border-zinc-700 bg-zinc-800/60 text-zinc-400 hover:border-zinc-600 hover:text-zinc-300'}`}
                      >
                        {plan.showOnLanding ? 'Hide from landing' : 'Show on landing'}
                      </button>
                      <button
                        type="button"
                        disabled={mutatingPlans}
                        onClick={() => savePlan(plan.id, { isPopular: !plan.isPopular })}
                        className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border text-[11px] font-medium transition-colors disabled:opacity-50 ${plan.isPopular ? 'border-fuchsia-500/30 bg-fuchsia-500/10 text-fuchsia-400 hover:bg-fuchsia-500/20' : 'border-zinc-700 bg-zinc-800/60 text-zinc-400 hover:border-zinc-600 hover:text-zinc-300'}`}
                      >
                        {plan.isPopular ? 'Unmark popular' : 'Mark popular'}
                      </button>
                      <div className="ml-auto flex items-center gap-2">
                        {plan.amount > 0 && (
                          <button
                            type="button"
                            disabled={syncingPlanId === plan.id || mutatingPlans}
                            onClick={() => handleSyncPlanToRazorpay(plan.id)}
                            className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border text-[11px] font-medium transition-colors disabled:opacity-50 ${plan.razorpayPlanId ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20' : 'border-amber-500/40 bg-amber-500/10 text-amber-400 hover:bg-amber-500/20'}`}
                          >
                            {syncingPlanId === plan.id ? (
                              <><Loader2 className="w-3 h-3 animate-spin" /> Syncing…</>
                            ) : plan.razorpayPlanId ? (
                              <>⚡ Re-sync to Razorpay</>
                            ) : (
                              <>⚡ Sync to Razorpay</>
                            )}
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={(event) => openActionMenu(event, [
                            {
                              label: 'Edit plan',
                              onClick: () => {
                                startPlanEdit(plan);
                                setOpenMenu(null);
                              },
                            },
                            {
                              label: 'Duplicate plan',
                              onClick: () => {
                                setOpenMenu(null);
                                duplicatePlan(plan);
                              },
                            },
                            {
                              label: 'Delete plan',
                              tone: 'danger',
                              onClick: () => {
                                setOpenMenu(null);
                                setConfirmAction({
                                  title: 'Delete this plan?',
                                  message: 'If the plan is already assigned, it will be deactivated instead of deleted.',
                                  confirmLabel: 'Delete plan',
                                  tone: 'danger',
                                  action: () => handleDeletePlan(plan.id),
                                });
                              },
                            },
                          ])}
                          className="h-9 w-9 inline-flex items-center justify-center rounded-lg border border-zinc-700 text-zinc-300 hover:text-zinc-100"
                        >
                          <MoreHorizontal className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                )) : null}
              </div>
              <PaginationControls pagination={plansPagination} onPageChange={setPlanPage} />
            </section>
          </div>

          {/* ── Offers section ── */}
          <section className="rounded-2xl border border-zinc-800 bg-zinc-900 p-5">
            <div className="mb-4 flex items-start justify-between gap-4">
              <div>
                <h2 className="text-lg font-semibold text-zinc-100 flex items-center gap-2">
                  <Tag className="w-4 h-4 text-emerald-400" /> Offers &amp; Discounts
                </h2>
                <p className="text-xs text-zinc-500">Promotional discount offers for this product. Active offers surface as banners to users.</p>
              </div>
              <button
                type="button"
                onClick={openCreateOfferModal}
                className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border border-emerald-600/50 bg-emerald-600/10 text-emerald-400 hover:bg-emerald-600/20 transition-colors text-xs font-semibold"
              >
                <Tag className="w-3 h-3" /> New offer
              </button>
            </div>
            {loadingOffers ? (
              <div className="flex items-center gap-2 text-sm text-zinc-400">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading offers…
              </div>
            ) : null}
            {!loadingOffers && offers.length === 0 ? (
              <div className="rounded-xl border border-dashed border-zinc-700 bg-zinc-950/40 p-6 text-sm text-zinc-500">
                No offers yet. Create one to start running a promotional discount.
              </div>
            ) : null}
            <div className="space-y-3">
              {offers.map((offer) => {
                const isExpired = offer.validUntil ? new Date(offer.validUntil) < new Date() : false;
                const isFull = offer.maxRedemptions != null && offer.currentRedemptions >= offer.maxRedemptions;
                return (
                  <div key={offer.id} className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm font-semibold text-zinc-100">{offer.title}</p>
                          <span className="text-[11px] font-mono text-zinc-500">{offer.code}</span>
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-600/20 text-emerald-300 border border-emerald-600/30">
                            {offer.discountLabel}
                          </span>
                        </div>
                        {offer.description ? <p className="text-xs text-zinc-400 mt-1">{offer.description}</p> : null}
                        <p className="text-[11px] text-zinc-500 mt-1.5 italic">{offer.bannerText}</p>
                      </div>
                      <div className="flex-shrink-0 flex items-center gap-2">
                        <span className={`px-2 py-1 rounded-full border text-[10px] font-semibold ${
                          offer.status !== 'active' || isExpired || isFull
                            ? 'border-zinc-700 bg-zinc-800 text-zinc-500'
                            : 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400'
                        }`}>
                          {isExpired ? 'expired' : isFull ? 'limit reached' : offer.status}
                        </span>
                        <button
                          type="button"
                          disabled={mutatingOffers}
                          onClick={(e) => openActionMenu(e, [
                            { label: 'Edit offer', onClick: () => { setOpenMenu(null); openEditOfferModal(offer); } },
                            { label: 'Duplicate offer', onClick: () => { setOpenMenu(null); handleDuplicateOffer(offer); } },
                            {
                              label: offer.status === 'active' ? 'Deactivate' : 'Activate',
                              onClick: () => {
                                setOpenMenu(null);
                                updateOffer(offer.id, { status: offer.status === 'active' ? 'inactive' : 'active' })
                                  .then(() => toast.success('Offer status updated.'))
                                  .catch((err: any) => toast.error(err?.message || 'Failed.'));
                              },
                            },
                            {
                              label: 'Delete offer', tone: 'danger',
                              onClick: () => {
                                setOpenMenu(null);
                                setConfirmAction({
                                  title: 'Delete this offer?',
                                  message: 'This will permanently delete the offer. Users currently in checkout won\'t be affected.',
                                  confirmLabel: 'Delete offer',
                                  tone: 'danger',
                                  action: async () => {
                                    await removeOffer(offer.id);
                                    toast.success('Offer deleted.');
                                  },
                                });
                              },
                            },
                          ])}
                          className="h-8 w-8 inline-flex items-center justify-center rounded-lg border border-zinc-700 text-zinc-300 hover:text-zinc-100"
                        >
                          <MoreHorizontal className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2 text-[11px]">
                      <span className="px-2 py-1 rounded-full border border-zinc-700 bg-zinc-900 text-zinc-400">
                        From {new Date(offer.validFrom).toLocaleDateString()}
                        {offer.validUntil ? ` → ${new Date(offer.validUntil).toLocaleDateString()}` : ' (no expiry)'}
                      </span>
                      {offer.maxRedemptions != null ? (
                        <span className="px-2 py-1 rounded-full border border-zinc-700 bg-zinc-900 text-zinc-400">
                          {offer.currentRedemptions} / {offer.maxRedemptions} redeemed
                        </span>
                      ) : (
                        <span className="px-2 py-1 rounded-full border border-zinc-700 bg-zinc-900 text-zinc-400">Unlimited redemptions</span>
                      )}
                      <span className="px-2 py-1 rounded-full border border-zinc-700 bg-zinc-900 text-zinc-400">All paid plans</span>
                      <span className={`px-2 py-1 rounded-full border ${offer.showBanner ? 'border-sky-500/30 bg-sky-500/10 text-sky-400' : 'border-zinc-600 bg-zinc-800/60 text-zinc-500'}`}>
                        Banner: {offer.showBanner ? 'on' : 'off'}
                      </span>
                      {/* Razorpay per-method linking */}
                      <span className="flex items-center gap-2 text-zinc-500">
                        {(() => {
                          const linkedCount = PAYMENT_METHODS.filter(m => offer.razorpayOfferIds?.[m.key]).length;
                          return (
                            <button
                              type="button"
                              onClick={() => setExpandedRzpOffer(expandedRzpOffer === offer.id ? null : offer.id)}
                              className={`inline-flex items-center gap-1.5 h-6 px-2 rounded border text-[10px] font-medium transition-colors ${
                                linkedCount > 0
                                  ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20'
                                  : 'border-amber-500/40 bg-amber-500/10 text-amber-400 hover:bg-amber-500/20'
                              }`}
                            >
                              ⚡ Razorpay IDs{linkedCount > 0 ? ` (${linkedCount}/4 linked)` : ' (none linked)'}
                            </button>
                          );
                        })()}
                      </span>
                      {expandedRzpOffer === offer.id && (
                        <div className="mt-2 w-full rounded-xl border border-zinc-700/60 bg-zinc-950 p-3 space-y-2">
                          <p className="text-[10px] text-zinc-500 mb-1">Create a separate offer in Razorpay dashboard for each payment method, then paste the IDs below.</p>
                          {PAYMENT_METHODS.map(({ key, label, icon }) => {
                            const mk = `${offer.id}:${key}`;
                            const linked: string[] = offer.razorpayOfferIds?.[key] || [];
                            const linkedStr = linked.join(', ');
                            const inputVal = methodInputMap[mk] ?? linkedStr;
                            const isSaving = syncingKey === mk;
                            return (
                              <div key={key} className="space-y-1">
                                <div className="flex items-center gap-2">
                                  <span className="w-24 text-[11px] text-zinc-400 flex-shrink-0">{icon} {label}</span>
                                  <input
                                    type="text"
                                    value={inputVal}
                                    onChange={(e) => setMethodInputMap((p) => ({ ...p, [mk]: e.target.value }))}
                                    onKeyDown={(e) => { if (e.key === 'Enter') handleSyncMethodOffer(offer.id, key); }}
                                    placeholder="off_XXX or off_XXX, off_YYY"
                                    className="flex-1 h-7 px-2 rounded border border-zinc-700 bg-zinc-900 text-zinc-200 text-[10px] font-mono focus:outline-none focus:border-zinc-500"
                                  />
                                  <button
                                    type="button"
                                    disabled={isSaving || inputVal === linkedStr}
                                    onClick={() => handleSyncMethodOffer(offer.id, key)}
                                    className="h-7 px-2.5 rounded border border-emerald-500/40 bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 text-[10px] font-medium transition-colors disabled:opacity-40 flex-shrink-0"
                                  >
                                    {isSaving ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : linked.length ? 'Update' : 'Save'}
                                  </button>
                                </div>
                                {linked.length > 0 && (
                                  <div className="flex flex-wrap gap-1 pl-[6.5rem]">
                                    {linked.map((id) => (
                                      <span key={id} className="inline-flex items-center gap-1 rounded bg-emerald-500/10 border border-emerald-500/20 px-1.5 py-0.5 text-[9px] font-mono text-emerald-400">
                                        ✓ {id}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          <section className="rounded-2xl border border-zinc-800 bg-zinc-900 p-5">
            <h2 className="text-lg font-semibold text-zinc-100">Customer Subscriptions</h2>
            <input
              value={subscriptionQuery}
              onChange={(e) => {
                setSubscriptionQuery(e.target.value);
                setSubscriptionPage(1);
              }}
              placeholder="Search subscriptions"
              className="mt-3 w-full max-w-sm h-10 px-3 bg-zinc-950 border border-zinc-700 rounded-xl text-sm text-zinc-100"
            />
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-zinc-500 border-b border-zinc-800">
                    <th className="py-2 pr-4">Customer</th>
                    <th className="py-2 pr-4">Plan</th>
                    <th className="py-2 pr-4">Status</th>
                    <th className="py-2 pr-4">Usage</th>
                    <th className="py-2 pr-4">Current End</th>
                    <th className="py-2">Razorpay ID</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingSubscriptions ? (
                    <tr>
                      <td className="py-8 text-center text-zinc-400" colSpan={6}>
                        <span className="inline-flex items-center gap-2">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Loading customer subscriptions...
                        </span>
                      </td>
                    </tr>
                  ) : null}
                  {!loadingSubscriptions && subscriptions.length === 0 ? (
                    <tr>
                      <td className="py-8 text-center text-zinc-500" colSpan={6}>
                        No customer subscriptions found.
                      </td>
                    </tr>
                  ) : null}
                  {!loadingSubscriptions ? subscriptions.map((subscription) => (
                    <tr key={subscription.id} className="border-b border-zinc-800/70 align-top">
                      <td className="py-3 pr-4">
                        <p className="text-zinc-100">{subscription.name || 'Unknown user'}</p>
                        <p className="text-xs text-zinc-500">{subscription.email}</p>
                      </td>
                      <td className="py-3 pr-4 text-zinc-300">{subscription.plan?.name || '—'}</td>
                      <td className="py-3 pr-4">
                        <div className="flex flex-wrap gap-2">
                          <span className="px-2 py-1 flex items-center gap-1.5 rounded-full border border-zinc-700 bg-zinc-950 text-xs text-zinc-300">
                            {subscription.status}
                            {subscription.cancelAtCycleEnd && ['active', 'cancelled'].includes(subscription.status) ? <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" title="Cancels at period end" /> : null}
                            {subscription.cancelAtCycleEnd && ['active', 'cancelled'].includes(subscription.status) ? <span className="text-amber-500/80"> (ends at cycle end)</span> : null}
                          </span>
                          {subscription.isSyntheticFreeTier ? (
                            <span className="px-2 py-1 rounded-full border border-sky-500/30 bg-sky-500/10 text-xs text-sky-300">Free tier</span>
                          ) : null}
                        </div>
                      </td>
                      <td className="py-3 pr-4 text-zinc-400 text-xs">
                        {(() => {
                          const isYearly = subscription.plan?.intervalUnit === 'yearly';
                          const periodLabel = isYearly ? 'Annual' : 'Monthly';
                          return (
                            <div className="space-y-0.5">
                              <div>
                                {subscription.maxAnalysesPerMonth == null
                                  ? <span>Analyses: Unlimited</span>
                                  : <span>Analyses ({periodLabel}): {subscription.analysesUsedThisMonth || 0} / {subscription.maxAnalysesPerMonth}</span>}
                              </div>
                              {subscription.maxReanalysesPerMonth != null && (
                                <div className="text-zinc-500">
                                  Re-analyses ({periodLabel}): {subscription.reanalysesUsedThisMonth || 0} / {subscription.maxReanalysesPerMonth}
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                      <td className="py-3 pr-4 text-zinc-400">{subscription.currentEnd ? new Date(subscription.currentEnd).toLocaleString() : '—'}</td>
                      <td className="py-3 text-zinc-500 text-xs">
                        <div className="flex items-center gap-2 justify-between">
                          <span>{subscription.providerSubscriptionId || (subscription.isSyntheticFreeTier ? 'internal-free' : 'manual')}</span>
                          {/* Active free-tier: revoke + optional quota reset */}
                          {subscription.isSyntheticFreeTier && subscription.status !== 'revoked' ? (
                            <button
                              type="button"
                              onClick={(event) => openActionMenu(event, [
                                ...(subscription.product?.code ? [{
                                  label: 'Reset quota',
                                  disabled: mutatingSubscriptions || ((subscription.analysesUsedThisMonth || 0) <= 0 && (subscription.reanalysesUsedThisMonth || 0) <= 0),
                                  onClick: () => {
                                    setOpenMenu(null);
                                    setConfirmAction({
                                      title: 'Reset total usage quota?',
                                      message: `This will reset all recorded usage (analyses + re-analyses) for ${subscription.email} on the free tier.`,
                                      confirmLabel: 'Reset quota',
                                      action: () => handleResetMonthlyQuota(subscription.userId, subscription.product!.code, 'both'),
                                    });
                                  },
                                }] : []),
                                ...(typeof subscription.id === 'number' ? [{
                                  label: 'Revoke free access',
                                  tone: 'danger' as const,
                                  disabled: mutatingSubscriptions,
                                  onClick: () => {
                                    setOpenMenu(null);
                                    handleRevokeSubscription(Number(subscription.id), subscription.email);
                                  },
                                }] : []),
                              ])}
                              className="h-8 w-8 inline-flex items-center justify-center rounded-lg border border-zinc-700 text-zinc-300 hover:text-zinc-100"
                            >
                              <MoreHorizontal className="w-4 h-4" />
                            </button>
                          ) : null}
                          {/* Revoked free-tier: reset quota only */}
                          {subscription.isSyntheticFreeTier && subscription.status === 'revoked' ? (
                            <button
                              type="button"
                              onClick={(event) => {
                                const items = subscription.product?.code ? [{
                                  label: 'Reset quota',
                                  disabled: mutatingSubscriptions || ((subscription.analysesUsedThisMonth || 0) <= 0 && (subscription.reanalysesUsedThisMonth || 0) <= 0),
                                  onClick: () => {
                                    setOpenMenu(null);
                                    setConfirmAction({
                                      title: 'Reset total usage quota?',
                                      message: `This will reset all recorded usage (analyses + re-analyses) for ${subscription.email} on the free tier.`,
                                      confirmLabel: 'Reset quota',
                                      action: () => handleResetMonthlyQuota(subscription.userId, subscription.product!.code, 'both'),
                                    });
                                  },
                                }] : [];
                                openActionMenu(event, items);
                              }}
                              className="h-8 w-8 inline-flex items-center justify-center rounded-lg border border-zinc-700 text-zinc-300 hover:text-zinc-100"
                            >
                              <MoreHorizontal className="w-4 h-4" />
                            </button>
                          ) : null}
                          {/* Normal subscriptions: full action menu */}
                          {subscription.status !== 'revoked' && !subscription.isSyntheticFreeTier ? (
                            <div>
                              <button
                                type="button"
                                onClick={(event) => openActionMenu(event, [
                                  ...(subscription.product?.code && (subscription.maxAnalysesPerMonth != null || subscription.maxReanalysesPerMonth != null) ? [{
                                    label: 'Reset quota',
                                    disabled: mutatingSubscriptions || ((subscription.analysesUsedThisMonth || 0) <= 0 && (subscription.reanalysesUsedThisMonth || 0) <= 0),
                                    onClick: () => {
                                      setOpenMenu(null);
                                      setConfirmAction({
                                        title: 'Reset monthly quota?',
                                        message: `This will reset the current month's analyses and re-analyses usage for ${subscription.email}.`,
                                        confirmLabel: 'Reset quota',
                                        action: () => handleResetMonthlyQuota(subscription.userId, subscription.product!.code, 'both'),
                                      });
                                    },
                                  }] : []),
                                  ...(typeof subscription.id === 'number' ? [{
                                    label: 'Cancel subscription',
                                    tone: 'danger' as const,
                                    disabled: mutatingSubscriptions,
                                    onClick: () => {
                                      setOpenMenu(null);
                                      handleRevokeSubscription(Number(subscription.id), subscription.email);
                                    },
                                  }] : []),
                                ])}
                                className="h-8 w-8 inline-flex items-center justify-center rounded-lg border border-zinc-700 text-zinc-300 hover:text-zinc-100"
                              >
                                <MoreHorizontal className="w-4 h-4" />
                              </button>
                            </div>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  )) : null}
                </tbody>
              </table>
            </div>
            <PaginationControls pagination={subscriptionsPagination} onPageChange={setSubscriptionPage} />
          </section>
        </>
      ) : null}

      {revokeTarget ? (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 px-4" onClick={(e) => { if (e.target === e.currentTarget) setRevokeTarget(null); }}>
          <div className="w-full max-w-md rounded-2xl border border-zinc-800 bg-zinc-900 p-6 shadow-2xl">
            <h3 className="text-xl font-bold text-zinc-100">Cancel Subscription?</h3>
            <p className="mt-2 text-sm text-zinc-400">
              Important: This action cannot be undone. The subscription for <span className="text-zinc-200 font-semibold">{revokeTarget.email}</span> will move to a cancelled state.
            </p>
            
            <div className="mt-6 space-y-3">
              <label className="flex items-start gap-3 cursor-pointer p-3 rounded-lg border border-zinc-800 bg-zinc-950/50 hover:bg-zinc-800/50 transition-colors">
                <input 
                  type="radio" 
                  name="cancelType" 
                  className="mt-1" 
                  checked={cancelAtCycleEnd === true} 
                  onChange={() => setCancelAtCycleEnd(true)} 
                />
                <div>
                  <p className="text-sm font-semibold text-zinc-200">Cancel at end of current billing cycle</p>
                  <p className="text-xs text-zinc-500 mt-0.5">Next payment will not be charged. User retains access until the cycle ends.</p>
                </div>
              </label>

              <label className="flex items-start gap-3 cursor-pointer p-3 rounded-lg border border-red-900/20 bg-red-950/10 hover:bg-red-900/20 transition-colors">
                <input 
                  type="radio" 
                  name="cancelType" 
                  className="mt-1" 
                  checked={cancelAtCycleEnd === false} 
                  onChange={() => setCancelAtCycleEnd(false)} 
                />
                <div>
                  <p className="text-sm font-semibold text-red-400">Cancel Immediately</p>
                  <p className="text-xs text-red-500/70 mt-0.5">Revokes access instantly. No prorated refund will be issued.</p>
                </div>
              </label>
            </div>

            <div className="mt-6 flex items-center justify-end gap-3 pt-4 border-t border-zinc-800">
              <button
                type="button"
                onClick={() => setRevokeTarget(null)}
                disabled={mutatingSubscriptions}
                className="h-10 px-4 flex items-center justify-center rounded-xl border border-zinc-700 text-zinc-300 text-sm hover:text-white hover:bg-zinc-800 transition-colors disabled:opacity-50"
              >
                No, don't
              </button>
              <button
                type="button"
                onClick={executeRevoke}
                disabled={mutatingSubscriptions}
                className="h-10 px-6 flex items-center justify-center rounded-xl font-semibold bg-blue-600 hover:bg-blue-500 text-white transition-colors disabled:opacity-50 gap-2"
              >
                {mutatingSubscriptions ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                Yes, Cancel
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {confirmAction ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4">
          <div className="w-full max-w-md rounded-2xl border border-zinc-800 bg-zinc-900 p-5 shadow-2xl">
            <h3 className="text-base font-semibold text-zinc-100">{confirmAction.title}</h3>
            <p className="mt-2 text-sm text-zinc-400 leading-relaxed">{confirmAction.message}</p>
            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmAction(null)}
                className="h-10 px-4 rounded-xl border border-zinc-700 text-zinc-300 text-sm"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => { runConfirmedAction().catch(() => undefined); }}
                className={`h-10 px-4 rounded-xl text-sm font-semibold ${
                  confirmAction.tone === 'danger'
                    ? 'bg-red-500/90 hover:bg-red-500 text-white'
                    : 'bg-amber-500 hover:bg-amber-600 text-black'
                }`}
              >
                {confirmAction.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {(createPlanOpen || editingPlanId !== null) ? createPortal(
        <div className="fixed inset-0 z-[55] flex items-center justify-center bg-black/70 px-4 py-8">
          <div className="w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl border border-zinc-800 bg-zinc-900 p-5 shadow-2xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-lg font-semibold text-zinc-100 flex items-center gap-2">
                  {editingPlanId !== null ? 'Edit Plan' : draft.name.startsWith('Copy of ') ? <><Copy className="w-4 h-4 text-amber-400" /> Duplicate Plan</> : 'Create Plan'}
                </h2>
                <p className="text-xs text-zinc-500 mt-1">
                  {editingPlanId !== null
                    ? 'Update the plan configuration and feature access for the selected product.'
                    : draft.name.startsWith('Copy of ')
                      ? 'All settings are copied from the source plan. Give it a unique code and adjust as needed.'
                      : 'Creates the local plan and syncs it to Razorpay for the selected product.'}
                </p>
              </div>
              <button
                type="button"
                onClick={closePlanModal}
                className="h-9 px-3 rounded-lg border border-zinc-700 text-zinc-300 text-xs"
              >
                Close
              </button>
            </div>

            <form onSubmit={editingPlanId !== null ? (event) => { event.preventDefault(); handleSavePlan(editingPlanId); } : handleCreatePlan} className="mt-5 space-y-3">
              {editingPlanId !== null ? (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <input value={editingPlanDraft.name} onChange={(e) => setEditingPlanDraft((prev) => ({ ...prev, name: e.target.value }))} placeholder="plan name" className="w-full h-10 px-3 bg-zinc-950 border border-zinc-700 rounded-xl text-sm text-zinc-100" />
                    <input value={editingPlanDraft.currency} onChange={(e) => setEditingPlanDraft((prev) => ({ ...prev, currency: e.target.value.toUpperCase() }))} placeholder="currency" className="h-10 px-3 bg-zinc-950 border border-zinc-700 rounded-xl text-sm text-zinc-100" />
                  </div>
                  <textarea value={editingPlanDraft.description} onChange={(e) => setEditingPlanDraft((prev) => ({ ...prev, description: e.target.value }))} placeholder="description" className="w-full min-h-[88px] px-3 py-2 bg-zinc-950 border border-zinc-700 rounded-xl text-sm text-zinc-100" />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <input value={editingPlanDraft.amount} onChange={(e) => setEditingPlanDraft((prev) => ({ ...prev, amount: e.target.value }))} placeholder="amount in paise" className="h-10 px-3 bg-zinc-950 border border-zinc-700 rounded-xl text-sm text-zinc-100" />
                    <input value={editingPlanDraft.intervalCount} onChange={(e) => setEditingPlanDraft((prev) => ({ ...prev, intervalCount: e.target.value }))} placeholder="interval count" className="h-10 px-3 bg-zinc-950 border border-zinc-700 rounded-xl text-sm text-zinc-100" />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <select value={editingPlanDraft.intervalUnit} onChange={(e) => setEditingPlanDraft((prev) => ({ ...prev, intervalUnit: e.target.value }))} className="h-10 px-3 bg-zinc-950 border border-zinc-700 rounded-xl text-sm text-zinc-100">
                      <option value="monthly">monthly</option>
                      <option value="yearly">yearly</option>
                      <option value="weekly">weekly</option>
                      <option value="daily">daily</option>
                    </select>
                    <select value={editingPlanDraft.status} onChange={(e) => setEditingPlanDraft((prev) => ({ ...prev, status: e.target.value }))} className="h-10 px-3 bg-zinc-950 border border-zinc-700 rounded-xl text-sm text-zinc-100">
                      <option value="active">active</option>
                      <option value="inactive">inactive</option>
                      <option value="draft">draft</option>
                    </select>
                  </div>
                  <div className="flex flex-wrap gap-4">
                    <label className="flex items-center gap-2 text-sm text-zinc-300">
                      <input type="checkbox" checked={editingPlanDraft.isDefault} onChange={(e) => setEditingPlanDraft((prev) => ({ ...prev, isDefault: e.target.checked }))} />
                      Mark as default
                    </label>
                    <label className="flex items-center gap-2 text-sm text-zinc-300">
                      <input type="checkbox" checked={editingPlanDraft.isPublic} onChange={(e) => setEditingPlanDraft((prev) => ({ ...prev, isPublic: e.target.checked }))} />
                      <span className="flex items-center gap-1.5">
                        {editingPlanDraft.isPublic ? <Eye className="w-3.5 h-3.5 text-sky-400" /> : <EyeOff className="w-3.5 h-3.5 text-zinc-500" />}
                        {editingPlanDraft.isPublic ? 'Visible to users (public)' : 'Admin-assign only (private)'}
                      </span>
                    </label>
                    <label className="flex items-center gap-2 text-sm text-zinc-300">
                      <input type="checkbox" checked={editingPlanDraft.showOnLanding} onChange={(e) => setEditingPlanDraft((prev) => ({ ...prev, showOnLanding: e.target.checked }))} />
                      Show on landing page
                    </label>
                    <label className="flex items-center gap-2 text-sm text-zinc-300">
                      <input type="checkbox" checked={editingPlanDraft.isPopular} onChange={(e) => setEditingPlanDraft((prev) => ({ ...prev, isPopular: e.target.checked }))} />
                      Mark as popular
                    </label>
                  </div>
                  {renderFeatureEditor(
                    activeFeatureSchema,
                    editingPlanDraft.features,
                    (updater) => setEditingPlanDraft((prev) => ({
                      ...prev,
                      features: typeof updater === 'function' ? updater(prev.features) : updater,
                    })),
                  )}
                </>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <input required value={draft.code} onChange={(e) => setDraft((prev) => ({ ...prev, code: e.target.value }))} placeholder="plan code (required)" className={`w-full h-10 px-3 bg-zinc-950 border rounded-xl text-sm text-zinc-100 ${!draft.code.trim() ? 'border-amber-500/50 focus:border-amber-400' : 'border-zinc-700'}`} />
                    <input value={draft.name} onChange={(e) => setDraft((prev) => ({ ...prev, name: e.target.value }))} placeholder="plan name" className="w-full h-10 px-3 bg-zinc-950 border border-zinc-700 rounded-xl text-sm text-zinc-100" />
                  </div>
                  <textarea value={draft.description} onChange={(e) => setDraft((prev) => ({ ...prev, description: e.target.value }))} placeholder="description" className="w-full min-h-[88px] px-3 py-2 bg-zinc-950 border border-zinc-700 rounded-xl text-sm text-zinc-100" />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <input value={draft.amount} onChange={(e) => setDraft((prev) => ({ ...prev, amount: e.target.value }))} placeholder="amount in paise" className="h-10 px-3 bg-zinc-950 border border-zinc-700 rounded-xl text-sm text-zinc-100" />
                    <input value={draft.currency} onChange={(e) => setDraft((prev) => ({ ...prev, currency: e.target.value.toUpperCase() }))} placeholder="currency" className="h-10 px-3 bg-zinc-950 border border-zinc-700 rounded-xl text-sm text-zinc-100" />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <select value={draft.intervalUnit} onChange={(e) => setDraft((prev) => ({ ...prev, intervalUnit: e.target.value }))} className="h-10 px-3 bg-zinc-950 border border-zinc-700 rounded-xl text-sm text-zinc-100">
                      <option value="monthly">monthly</option>
                      <option value="yearly">yearly</option>
                      <option value="weekly">weekly</option>
                      <option value="daily">daily</option>
                    </select>
                    <input value={draft.intervalCount} onChange={(e) => setDraft((prev) => ({ ...prev, intervalCount: e.target.value }))} placeholder="interval count" className="h-10 px-3 bg-zinc-950 border border-zinc-700 rounded-xl text-sm text-zinc-100" />
                  </div>
                  <div className="flex flex-wrap gap-4">
                    <label className="flex items-center gap-2 text-sm text-zinc-300">
                      <input type="checkbox" checked={draft.isDefault} onChange={(e) => setDraft((prev) => ({ ...prev, isDefault: e.target.checked }))} />
                      Mark as default
                    </label>
                    <label className="flex items-center gap-2 text-sm text-zinc-300">
                      <input type="checkbox" checked={draft.isPublic} onChange={(e) => setDraft((prev) => ({ ...prev, isPublic: e.target.checked }))} />
                      <span className="flex items-center gap-1.5">
                        {draft.isPublic ? <Eye className="w-3.5 h-3.5 text-sky-400" /> : <EyeOff className="w-3.5 h-3.5 text-zinc-500" />}
                        {draft.isPublic ? 'Visible to users (public)' : 'Admin-assign only (private)'}
                      </span>
                    </label>
                    <label className="flex items-center gap-2 text-sm text-zinc-300">
                      <input type="checkbox" checked={draft.showOnLanding} onChange={(e) => setDraft((prev) => ({ ...prev, showOnLanding: e.target.checked }))} />
                      Show on landing page
                    </label>
                    <label className="flex items-center gap-2 text-sm text-zinc-300">
                      <input type="checkbox" checked={draft.isPopular} onChange={(e) => setDraft((prev) => ({ ...prev, isPopular: e.target.checked }))} />
                      Mark as popular
                    </label>
                  </div>
                  {renderFeatureEditor(
                    activeFeatureSchema,
                    draft.features,
                    (updater) => setDraft((prev) => ({
                      ...prev,
                      features: typeof updater === 'function' ? updater(prev.features) : updater,
                    })),
                  )}
                </>
              )}
              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={closePlanModal}
                  className="h-10 px-4 rounded-xl border border-zinc-700 text-zinc-300 text-sm"
                >
                  Cancel
                </button>
                <button type="submit" disabled={creatingPlan || mutatingPlans} className="h-10 px-4 rounded-xl bg-amber-500 hover:bg-amber-600 text-black font-semibold text-sm disabled:opacity-50">
                  {editingPlanId !== null ? (mutatingPlans ? 'Saving plan…' : 'Save Plan') : (creatingPlan ? 'Creating plan…' : 'Create Subscription Plan')}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body,
      ) : null}
      {openMenu ? createPortal(
        <div
          className="fixed z-[60] w-44 rounded-xl border border-zinc-700 bg-zinc-950 shadow-2xl overflow-hidden"
          style={{ top: openMenu.top, right: openMenu.right }}
          onMouseDown={(event) => event.stopPropagation()}
        >
          {openMenu.items.map((item) => (
            <button
              key={item.label}
              type="button"
              disabled={item.disabled}
              onClick={item.onClick}
              className={`w-full px-3 py-2.5 text-left text-xs disabled:opacity-50 ${
                item.tone === 'danger'
                  ? 'text-red-300 hover:bg-red-500/10'
                  : 'text-zinc-200 hover:bg-zinc-900'
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>,
        document.body,
      ) : null}

      {/* ── Offer create/edit modal ── */}
      {offerModalOpen ? createPortal(
        <div className="fixed inset-0 z-[55] flex items-center justify-center bg-black/70 px-4 py-8">
          <div className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl border border-zinc-800 bg-zinc-900 p-5 shadow-2xl">
            <div className="flex items-start justify-between gap-4 mb-5">
              <div>
                <h2 className="text-lg font-semibold text-zinc-100 flex items-center gap-2">
                  <Tag className="w-4 h-4 text-emerald-400" />
                  {editingOfferId ? 'Edit Offer' : 'Create Offer'}
                </h2>
                <p className="text-xs text-zinc-500 mt-1">
                  {editingOfferId
                    ? 'Update the offer configuration. Changes take effect immediately.'
                    : 'Creates a discount offer and attempts to sync it with Razorpay.'}
                </p>
              </div>
              <button type="button" onClick={closeOfferModal} className="h-8 w-8 inline-flex items-center justify-center rounded-lg border border-zinc-700 text-zinc-400">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
            <form onSubmit={editingOfferId ? handleUpdateOffer : handleCreateOffer} className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] text-zinc-500">Offer code *</label>
                  <input
                    required
                    disabled={Boolean(editingOfferId)}
                    value={offerDraft.code}
                    onChange={(e) => setOfferDraft((p) => ({ ...p, code: e.target.value }))}
                    placeholder="e.g. LAUNCH10"
                    className="mt-1 w-full h-10 px-3 bg-zinc-950 border border-zinc-700 rounded-xl text-sm text-zinc-100 disabled:opacity-60"
                  />
                </div>
                <div>
                  <label className="text-[11px] text-zinc-500">Title *</label>
                  <input
                    required
                    value={offerDraft.title}
                    onChange={(e) => setOfferDraft((p) => ({ ...p, title: e.target.value }))}
                    placeholder="e.g. Launch discount"
                    className="mt-1 w-full h-10 px-3 bg-zinc-950 border border-zinc-700 rounded-xl text-sm text-zinc-100"
                  />
                </div>
              </div>
              <div>
                <label className="text-[11px] text-zinc-500">Description</label>
                <textarea
                  value={offerDraft.description}
                  onChange={(e) => setOfferDraft((p) => ({ ...p, description: e.target.value }))}
                  placeholder="Short description shown in the offer card"
                  className="mt-1 w-full min-h-[72px] px-3 py-2 bg-zinc-950 border border-zinc-700 rounded-xl text-sm text-zinc-100"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] text-zinc-500">Discount type *</label>
                  <select
                    value={offerDraft.discountType}
                    onChange={(e) => setOfferDraft((p) => ({ ...p, discountType: e.target.value as 'percentage' | 'fixed' }))}
                    className="mt-1 w-full h-10 px-3 bg-zinc-950 border border-zinc-700 rounded-xl text-sm text-zinc-100"
                  >
                    <option value="percentage">Percentage (%)</option>
                    <option value="fixed">Fixed amount (paise)</option>
                  </select>
                </div>
                <div>
                  <label className="text-[11px] text-zinc-500">
                    {offerDraft.discountType === 'percentage' ? 'Discount % (e.g. 15 for 15% off)' : 'Discount ₹ (e.g. 100 for ₹100 off)'}
                  </label>
                  <input
                    required
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={offerDraft.discountValue}
                    onChange={(e) => setOfferDraft((p) => ({ ...p, discountValue: e.target.value }))}
                    className="mt-1 w-full h-10 px-3 bg-zinc-950 border border-zinc-700 rounded-xl text-sm text-zinc-100"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] text-zinc-500">Valid from *</label>
                  <input
                    required
                    type="date"
                    value={offerDraft.validFrom}
                    onChange={(e) => setOfferDraft((p) => ({ ...p, validFrom: e.target.value }))}
                    className="mt-1 w-full h-10 px-3 bg-zinc-950 border border-zinc-700 rounded-xl text-sm text-zinc-100"
                  />
                </div>
                <div>
                  <label className="text-[11px] text-zinc-500">Valid until (leave blank = no expiry)</label>
                  <input
                    type="date"
                    value={offerDraft.validUntil}
                    onChange={(e) => setOfferDraft((p) => ({ ...p, validUntil: e.target.value }))}
                    className="mt-1 w-full h-10 px-3 bg-zinc-950 border border-zinc-700 rounded-xl text-sm text-zinc-100"
                  />
                </div>
              </div>
              <div>
                <label className="text-[11px] text-zinc-500">Max redemptions (leave blank = unlimited)</label>
                <input
                  type="number"
                  min="1"
                  value={offerDraft.maxRedemptions}
                  onChange={(e) => setOfferDraft((p) => ({ ...p, maxRedemptions: e.target.value }))}
                  placeholder="e.g. 100"
                  className="mt-1 w-full h-10 px-3 bg-zinc-950 border border-zinc-700 rounded-xl text-sm text-zinc-100"
                />
              </div>
              <div>
                <label className="text-[11px] text-zinc-500">Custom banner text (blank = auto-generated)</label>
                <input
                  value={offerDraft.bannerText}
                  onChange={(e) => setOfferDraft((p) => ({ ...p, bannerText: e.target.value }))}
                  placeholder="Auto-generated from title and discount if left blank"
                  className="mt-1 w-full h-10 px-3 bg-zinc-950 border border-zinc-700 rounded-xl text-sm text-zinc-100"
                />
              </div>
              <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={offerDraft.showBanner}
                  onChange={(e) => setOfferDraft((p) => ({ ...p, showBanner: e.target.checked }))}
                />
                Show promotional banner to users
              </label>
              <div className="flex items-center justify-end gap-2 pt-2 border-t border-zinc-800">
                <button type="button" onClick={closeOfferModal} className="h-10 px-4 rounded-xl border border-zinc-700 text-zinc-300 text-sm">
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={mutatingOffers}
                  className="h-10 px-4 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-semibold text-sm disabled:opacity-50 flex items-center gap-2"
                >
                  {mutatingOffers ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  {editingOfferId ? 'Save Offer' : 'Create Offer'}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body,
      ) : null}
    </AdminShell>
  );
}
